import React from 'react';
import {observable, action, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import * as checkoutUtil from '../../checkoutUtil';
import {Portal} from '../../../util';
import {IAddress, ICheckoutStep, IFormInput} from '../../i';
import {isCheckoutRedesign} from '../../ab-redesign';

@observer
export default class ShippingCorrection extends React.Component {
    @observable loading: boolean = false;

    constructor(props) {
        super(props);
        this.saveAddr = this.saveAddr.bind(this);
        this.useCorrection = this.useCorrection.bind(this);
        this.loading = false;
        makeObservable(this);
    }

    @action
    useCorrection = async () => {
        store.checkout.ship.addr.address1.value = store.checkout.ship.corr.address1.value;
        store.checkout.ship.addr.address2.value = store.checkout.ship.corr.address2.value;
        store.checkout.ship.addr.city.value = store.checkout.ship.corr.city.value;
        store.checkout.ship.addr.state.value = store.checkout.ship.corr.state.value;
        store.checkout.ship.addr.postalCode.value = store.checkout.ship.corr.postalCode.value;
        store.checkout.ship.addr.country.value = store.checkout.ship.corr.country.value;
        this.saveAddr(false);
    };

    @action
    saveAddr = async (ignored: boolean) => {
        store.checkout.ship.ignoreCorrection = true;
        this.loading = true;
        const success = await checkout('cc-ship-addr-submit');
        this.loading = false;
        checkoutUtil.checkDefaultShipping();
    };

    // B: one address block per panel; a field that FedEx changed is highlighted in both panels, as in the control.
    renderCorrAddress(src: IAddress) {
        const addr = store.checkout.ship.addr;
        const corr = store.checkout.ship.corr;
        const diff = (o: IFormInput) => (o.valid.isError ? 'ab-corr-diff' : undefined);
        return (
            <div className="ab-corr-addr">
                <div>
                    {addr.firstName.value} {addr.lastName.value}
                </div>
                <div className={diff(corr.address1)}>{src.address1.value}</div>
                {src.address2.value.length > 0 && <div className={diff(corr.address2)}>{src.address2.value}</div>}
                <div>
                    <span className={diff(corr.city)}>{src.city.value},</span> <span className={diff(corr.state)}>{src.state.value}</span> <span className={diff(corr.postalCode)}>{src.postalCode.value}</span> <span className={diff(corr.country)}>{src.country.value}</span>
                </div>
            </div>
        );
    }

    // B: the control's panel chrome (dotted borders, uppercase labels, fixed-height 45px buttons) does not fit the
    // redesigned card: 'FedEx Preferred Address' overflowed its button at phone widths. Same handlers, B markup.
    renderRedesign() {
        const ship = store.checkout.ship;
        const spinner = (
            <span className="pad-left">
                <i
                    className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                    aria-hidden={true}
                ></i>
            </span>
        );
        return (
            <div className="ab-corr">
                <div className="ab-corr-head">
                    <div className="ab-ship-lead">Please confirm your shipping address</div>
                    <button
                        type="button"
                        className="ab-add-link"
                        onClick={() => store.moveStep(ICheckoutStep.shipping)}
                        aria-label="Edit shipping address"
                    >
                        <i
                            className="far fa-pen"
                            aria-hidden={true}
                        />
                        Edit address
                    </button>
                </div>
                <div
                    aria-live="assertive"
                    aria-atomic="true"
                    className="sr-only"
                >
                    {this.loading ? 'Calculating shipping price' : ''}
                </div>
                <div className="ab-corr-panels">
                    <div className="ab-corr-panel">
                        <div className="ab-corr-label">You entered</div>
                        {this.renderCorrAddress(ship.addr)}
                        <div className="ab-corr-action">
                            <button
                                type="button"
                                className="btn-checkout btn-checkout-white"
                                aria-label="Use the address you entered"
                                aria-busy={this.loading}
                                disabled={this.loading}
                                onClick={() => this.saveAddr(true)}
                            >
                                {!this.loading ? 'Use address as entered' : 'Calculating'}
                                {this.loading && spinner}
                            </button>
                        </div>
                    </div>
                    {ship.corr != undefined && (
                        <div className="ab-corr-panel">
                            <div className="ab-corr-label">Suggested correction</div>
                            {this.renderCorrAddress(ship.corr)}
                            <div className="ab-corr-action">
                                <button
                                    type="button"
                                    className="btn-checkout btn-checkout-yellow"
                                    aria-label="Use the suggested corrected address"
                                    aria-busy={this.loading}
                                    disabled={this.loading}
                                    onClick={this.useCorrection}
                                >
                                    {!this.loading ? 'Use suggested address' : 'Calculating'}
                                    {this.loading && spinner}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // todo APO, privacy warning, country/international
    render() {
        if (isCheckoutRedesign(store.checkout.isMyFrames)) {
            return this.renderRedesign();
        }

        return (
            <div className="shipping-write">
                <div className="">
                    <div className="row">
                        <div
                            className="col-12 checkout-sh-2"
                            style={{color: '#222'}}
                        >
                            Please Confirm Your Shipping Address
                            <button
                                className="link text-btn b pointer"
                                style={{marginLeft: 12}}
                                onClick={() => store.moveStep(ICheckoutStep.shipping)}
                                aria-label="Edit shipping address"
                                type="button"
                            >
                                edit
                            </button>
                        </div>
                        <div className="col-12 col-lg-6">
                            <div className="corr-panel">
                                <div className="form-inline-label form-label-light">You Entered</div>
                                <div className="hr"></div>
                                <div>
                                    {store.checkout.ship.addr.firstName.value} {store.checkout.ship.addr.lastName.value}
                                </div>
                                <div className={store.checkout.ship.corr.address1.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.address1.value}</div>
                                {store.checkout.ship.addr.address2.value.length > 0 && <div className={store.checkout.ship.corr.address2.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.address2.value}</div>}
                                <div className="corr-addr">
                                    <span className={store.checkout.ship.corr.city.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.city.value},</span>
                                    <span className={store.checkout.ship.corr.state.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.state.value}</span>
                                    <span className={store.checkout.ship.corr.postalCode.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.postalCode.value}</span>
                                    <span className={store.checkout.ship.corr.country.valid.isError ? 'correction' : ''}>{store.checkout.ship.addr.country.value}</span>
                                </div>
                                <div className="hr"></div>
                                <div className="text-center">
                                    <div
                                        aria-live="assertive"
                                        aria-atomic="true"
                                        className="sr-only"
                                    >
                                        {this.loading ? 'Calculating shipping price' : ''}
                                    </div>
                                    <button
                                        type="button"
                                        className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                        aria-label="Use the address you entered"
                                        aria-busy={this.loading}
                                        disabled={this.loading}
                                        style={{backgroundColor: '#FFFFFF'}}
                                        onClick={() => this.saveAddr(true)}
                                    >
                                        {!this.loading ? 'Use This Address' : 'Calculating'}
                                        {this.loading && (
                                            <span className="pad-left">
                                                <i
                                                    className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                                    aria-hidden={true}
                                                ></i>
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {store.checkout.ship.corr != undefined && (
                            <div className="col-12 col-lg-6">
                                <div className="corr-panel">
                                    <div className="form-inline-label form-label-light">Suggested Correction</div>
                                    <div className="hr"></div>
                                    <div>
                                        {store.checkout.ship.addr.firstName.value} {store.checkout.ship.addr.lastName.value}
                                    </div>
                                    <div className={store.checkout.ship.corr.address1.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.address1.value}</div>
                                    {store.checkout.ship.corr.address2.value.length > 0 && <div className={store.checkout.ship.corr.address2.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.address2.value}</div>}
                                    <div className="corr-addr">
                                        <span className={store.checkout.ship.corr.city.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.city.value},</span>
                                        <span className={store.checkout.ship.corr.state.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.state.value}</span>
                                        <span className={store.checkout.ship.corr.postalCode.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.postalCode.value}</span>
                                        <span className={store.checkout.ship.corr.country.valid.isError ? 'correction' : ''}>{store.checkout.ship.corr.country.value}</span>
                                    </div>
                                    <div className="hr"></div>
                                    <div className="text-center">
                                        <div
                                            aria-live="assertive"
                                            aria-atomic="true"
                                            className="sr-only"
                                        >
                                            {this.loading ? 'Calculating shipping price' : ''}
                                        </div>
                                        <button
                                            type="button"
                                            className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                            aria-label="Use the suggested corrected address"
                                            onClick={this.useCorrection}
                                            aria-busy={this.loading}
                                            disabled={this.loading}
                                        >
                                            {!this.loading ? 'FedEx Preferred Address' : 'Calculating'}
                                            {this.loading && (
                                                <span className="pad-left">
                                                    <i
                                                        className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                                        aria-hidden={true}
                                                    ></i>
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
